import { TokenTypeEnum } from '@/services/auth/storage/types';
import {
    accessTokenKey,
    cognitoTokenKey,
    countryCodeKey,
    deprecatedAccessTokenKey,
    deprecatedChosenRestaurantIdKey,
    deprecatedCognitoTokenKey,
    deprecatedCountryCodeKey,
    deprecatedLoginModeKey,
    deprecatedPosVendorKey,
    deprecatedRefreshTokenKey,
    deprecatedTimezoneKey,
    deprecatedUserIdKey,
    deprecatedUsernameKey,
    deprecatedUserRoleKey,
    posVendorKey,
    refreshTokenKey,
    restaurantIdKey,
    timezoneKey,
    tokenTypeKey,
    userIdKey,
    usernameKey,
    userRoleKey,
} from '@/services/auth/storage/consts';

class AuthStorageService {
    public static getInstance() {
        // @ts-ignore
        if (!process.browser) {
            return;
        }

        if (!this.instance) {
            this.instance = new AuthStorageService();
        }

        // eslint-disable-next-line consistent-return
        return this.instance;
    }

    private static instance: AuthStorageService;

    private tokenType: TokenTypeEnum | string | null;

    private accessToken: string | null;

    private refreshToken: string | null;

    private cognitoToken: string | null;

    private restaurantId: string | null;

    private posVendor: string | null;

    private userId: string | null;

    private username: string | null;

    private userRole: string | null;

    private countryCode: string | null;

    private timezone: string | null;

    constructor() {
        this.tokenType = (localStorage.getItem(tokenTypeKey) ||
            localStorage.getItem(deprecatedLoginModeKey)) as TokenTypeEnum;

        this.accessToken = localStorage.getItem(accessTokenKey) || localStorage.getItem(deprecatedAccessTokenKey);

        this.refreshToken = localStorage.getItem(refreshTokenKey) || localStorage.getItem(deprecatedRefreshTokenKey);

        this.cognitoToken = localStorage.getItem(cognitoTokenKey) || localStorage.getItem(deprecatedCognitoTokenKey);

        this.restaurantId =
            localStorage.getItem(restaurantIdKey) || localStorage.getItem(deprecatedChosenRestaurantIdKey);

        this.posVendor = localStorage.getItem(posVendorKey) || localStorage.getItem(deprecatedPosVendorKey);

        this.userId = localStorage.getItem(userIdKey) || localStorage.getItem(deprecatedUserIdKey);

        this.username = localStorage.getItem(usernameKey) || localStorage.getItem(deprecatedUsernameKey);

        this.userRole = localStorage.getItem(userRoleKey) || localStorage.getItem(deprecatedUserRoleKey);

        this.countryCode = localStorage.getItem(countryCodeKey) || localStorage.getItem(deprecatedCountryCodeKey);

        this.timezone = localStorage.getItem(timezoneKey) || localStorage.getItem(deprecatedTimezoneKey);
    }

    // Token Type
    public setTokenType(val: TokenTypeEnum, provider?: string) {
        if (val === TokenTypeEnum.Vendor) {
            this.tokenType = provider ? `${val}:${provider}` : val;
        } else {
            this.tokenType = val;
        }
        localStorage.setItem(tokenTypeKey, this.tokenType);
    }

    public getTokenType(): TokenTypeEnum | string | null {
        return this.tokenType;
    }

    public removeTokenType() {
        this.tokenType = null;
        localStorage.removeItem(tokenTypeKey);
        localStorage.removeItem(deprecatedLoginModeKey);
    }

    // Access Token
    public setAccessToken(val: string) {
        this.accessToken = val;
        localStorage.setItem(accessTokenKey, this.accessToken);
    }

    public getAccessToken(): string | null {
        return this.accessToken;
    }

    public removeAccessToken() {
        this.accessToken = null;
        localStorage.removeItem(accessTokenKey);
        localStorage.removeItem(deprecatedAccessTokenKey);
    }

    // Refresh Token
    public setRefreshToken(val: string) {
        this.refreshToken = val;
        localStorage.setItem(refreshTokenKey, this.refreshToken);
    }

    public getRefreshToken(): TokenTypeEnum | string | null {
        return this.refreshToken;
    }

    public removeRefreshToken() {
        this.refreshToken = null;
        localStorage.removeItem(refreshTokenKey);
        localStorage.removeItem(deprecatedRefreshTokenKey);
    }

    // Cognito Token
    public setCognitoToken(val: string) {
        this.cognitoToken = val;
        localStorage.setItem(cognitoTokenKey, this.cognitoToken);
    }

    public getCognitoToken(): string | null {
        return this.cognitoToken;
    }

    public removeCognitoToken() {
        this.cognitoToken = null;
        localStorage.removeItem(cognitoTokenKey);
        localStorage.removeItem(deprecatedCognitoTokenKey);
    }

    // Restaurant ID
    public setRestaurantId(val: string) {
        this.restaurantId = val;
        localStorage.setItem(restaurantIdKey, this.restaurantId);
    }

    public getRestaurantId(): string | null {
        return this.restaurantId;
    }

    public removeRestaurantId() {
        this.restaurantId = null;
        localStorage.removeItem(restaurantIdKey);
        localStorage.removeItem(deprecatedChosenRestaurantIdKey);
    }

    // POS Vendor
    public setPosVendor(val: string) {
        this.posVendor = val;
        localStorage.setItem(posVendorKey, this.posVendor);
    }

    public getPosVendor(): string | null {
        return this.posVendor;
    }

    public removePosVendor() {
        this.posVendor = null;
        localStorage.removeItem(posVendorKey);
        localStorage.removeItem(deprecatedPosVendorKey);
    }

    // User ID
    public setUserId(val: string) {
        this.userId = val;
        localStorage.setItem(userIdKey, this.userId);
    }

    public getUserId(): string | null {
        return this.userId;
    }

    public removeUserId() {
        this.userId = null;
        localStorage.removeItem(userIdKey);
        localStorage.removeItem(deprecatedUserIdKey);
    }

    // Username
    public setUsername(val: string) {
        this.username = val;
        localStorage.setItem(usernameKey, this.username);
    }

    public getUsername(): string | null {
        return this.username;
    }

    public removeUsername() {
        this.username = null;
        localStorage.removeItem(usernameKey);
        localStorage.removeItem(deprecatedUsernameKey);
    }

    // User Role
    public setUserRole(val: string) {
        this.userRole = val;
        localStorage.setItem(userRoleKey, this.userRole);
    }

    public getUserRole(): string | null {
        return this.userRole;
    }

    public removeUserRole() {
        this.userRole = null;
        localStorage.removeItem(userRoleKey);
        localStorage.removeItem(deprecatedUserRoleKey);
    }

    // Country Code
    public setCountryCode(val: string) {
        this.countryCode = val;
        localStorage.setItem(countryCodeKey, this.countryCode);
    }

    public getCountryCode(): string | null {
        return this.countryCode;
    }

    public removeCountryCode() {
        this.countryCode = null;
        localStorage.removeItem(countryCodeKey);
        localStorage.removeItem(deprecatedCountryCodeKey);
    }

    // Timezone
    public setTimezone(val: string) {
        this.timezone = val;
        localStorage.setItem(timezoneKey, this.timezone);
    }

    public getTimezone(): string | null {
        return this.timezone;
    }

    public removeTimezone() {
        this.timezone = null;
        localStorage.removeItem(timezoneKey);
        localStorage.removeItem(deprecatedTimezoneKey);
    }
}

// @ts-ignore
const AuthStorage: AuthStorageService = AuthStorageService.getInstance();

export default AuthStorage;
